/** Geometry helpers for FI snow contour digitizer (pixel space, Y-down). */

export const SK_COLORS = {
  2: '#0066cc',
  2.5: '#00aa44',
  2.75: '#cc8800',
  3: '#cc2222',
  3.5: '#8800cc',
};

/** Screen-space snap radius (matches Site Planner DXF viewport). */
export const SNAP_RADIUS_SCREEN_PX = 12;

export function skColor(sk) {
  return SK_COLORS[sk] || SK_COLORS[String(sk)] || '#ffffff';
}

export function dist(a, b) {
  const dx = a[0] - b[0];
  const dy = a[1] - b[1];
  return Math.hypot(dx, dy);
}

export function pointsNear(a, b, tol = 8) {
  return dist(a, b) <= tol;
}

export function projectOnSegment(p, a, b) {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len2 = dx * dx + dy * dy;
  if (len2 < 1e-9) {
    return { point: [a[0], a[1]], distance: dist(p, a) };
  }
  let t = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  const point = [a[0] + t * dx, a[1] + t * dy];
  return { point, distance: dist(p, point) };
}

function distToSegment(p, a, b) {
  return projectOnSegment(p, a, b).distance;
}

export function minPointsForContour(contour) {
  return contour?.closed ? 3 : 2;
}

export function canDeleteVertex(contour) {
  return contour.points.length > minPointsForContour(contour);
}

/** Snap to contour corners (priority) then open/closed edge segments. */
export function findSnapOnContourPolylines(cursorCoords, polylines, snapRadius) {
  if (!polylines?.length) return null;

  let bestSnap = null;
  let minCornerDist = snapRadius;

  polylines.forEach((line) => {
    (line.points || []).forEach((pt) => {
      const d = dist(cursorCoords, pt);
      if (d < minCornerDist) {
        minCornerDist = d;
        bestSnap = { point: [pt[0], pt[1]], type: 'corner' };
      }
    });
  });
  if (bestSnap) return bestSnap;

  let minEdgeDist = snapRadius;
  polylines.forEach((line) => {
    const pts = line.points || [];
    const n = pts.length;
    if (n < 2) return;
    const segCount = line.closed ? n : n - 1;
    for (let i = 0; i < segCount; i += 1) {
      const { point, distance } = projectOnSegment(cursorCoords, pts[i], pts[(i + 1) % n]);
      if (distance < minEdgeDist) {
        minEdgeDist = distance;
        bestSnap = { point, type: 'edge' };
      }
    }
  });
  return bestSnap;
}

export function getDrawSnapPolylines(contours, draftPoints) {
  const lines = contours.map((c) => ({ points: c.points, closed: c.closed }));
  if (draftPoints?.length >= 1) {
    lines.push({ points: draftPoints, closed: false });
  }
  return lines;
}

export function hitTestVertex(pixelPt, contours, scale, tolPx = 10) {
  const tol = tolPx / scale;
  for (let ci = contours.length - 1; ci >= 0; ci -= 1) {
    const c = contours[ci];
    for (let vi = 0; vi < c.points.length; vi += 1) {
      if (pointsNear(pixelPt, c.points[vi], tol)) {
        return { contourId: c.id, vertexIndex: vi };
      }
    }
  }
  return null;
}

export function hitTestContour(pixelPt, contours, scale, tolPx = 8) {
  const tol = tolPx / scale;
  for (let ci = contours.length - 1; ci >= 0; ci -= 1) {
    const c = contours[ci];
    const pts = c.points;
    const n = pts.length;
    if (n < 2) continue;
    const segCount = c.closed ? n : n - 1;
    for (let i = 0; i < segCount; i += 1) {
      const a = pts[i];
      const b = pts[(i + 1) % n];
      if (distToSegment(pixelPt, a, b) <= tol) {
        return c.id;
      }
    }
  }
  return null;
}

export function hitTestSegmentInsert(pixelPt, contours, scale, preferredContourId = null, tolPx = 8) {
  const tol = tolPx / scale;
  const minVertexGap = 6 / scale;
  let best = null;

  const tryContour = (c) => {
    const pts = c.points;
    const n = pts.length;
    if (n < 2) return;
    const segCount = c.closed ? n : n - 1;
    for (let i = 0; i < segCount; i += 1) {
      const a = pts[i];
      const b = pts[(i + 1) % n];
      const { point, distance } = projectOnSegment(pixelPt, a, b);
      if (distance > tol) continue;
      if (dist(point, a) < minVertexGap || dist(point, b) < minVertexGap) continue;
      if (!best || distance < best.distance) {
        best = {
          contourId: c.id,
          segmentIndex: i,
          insertIndex: i + 1,
          point,
          distance,
        };
      }
    }
  };

  if (preferredContourId) {
    const selected = contours.find((c) => c.id === preferredContourId);
    if (selected) tryContour(selected);
    if (best) return best;
  }

  for (let ci = contours.length - 1; ci >= 0; ci -= 1) {
    tryContour(contours[ci]);
  }
  return best;
}

export function hitTestProbePin(pixelPt, pins, scale, tolPx = 12) {
  const tol = tolPx / scale;
  for (let i = pins.length - 1; i >= 0; i -= 1) {
    const p = pins[i];
    const pt = [p.pixel_x ?? p.pixel?.[0], p.pixel_y ?? p.pixel?.[1]];
    if (pointsNear(pixelPt, pt, tol)) {
      return p.id;
    }
  }
  return null;
}

export function newContourId() {
  return `c_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

export function formatSk(sk) {
  return Number(sk) === 2.75 ? '2.75' : `${Number(sk)}`;
}
